// ============================================================================
// TEAM MEMBER SMART TASK TAB
// Admin-facing view for monitoring and overriding a team member's live
// SmartTask workflow state. Rendered inside WorkItemDetailModal when
// item_type === 'team_member' and SmartTask is enabled for the client.
// Contains: live status, appointment controls, today's schedule.
// Date: April 2026
// ============================================================================

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/total-operations/workflows/service-operations/team-member-detail.mdx
 * (published at user-docs.totalaccess.co.za/total-operations/workflows/service-operations/team-member-detail#smart-task)
 */

'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity, MapPin, Clock, Car, Navigation, Loader2,
  Calendar, CheckCircle2, RotateCcw,
  PlayCircle, PauseCircle, CalendarClock, Route, Info, AlertTriangle,
  Zap, MonitorSmartphone, Wifi, BatteryFull, Signal,
  ShieldAlert, ShieldCheck, ClipboardCheck,
} from 'lucide-react';
import type { WorkItem } from '../UniversalWorkItems';
import { authService } from '@/lib/authService';
import { notify } from '@/lib/notifications';
import { formatDateTimeSAST, formatTimeSAST } from '@/lib/sastTime';
import {
  getAllowedAppointmentAdminOverrideStatuses,
} from '@/lib/work-items/appointmentAdminOverrideTransitions';
import { formatAdminAppointmentOverrideSuccessMessage, prepareAdminAppointmentOverride } from '@/lib/work-items/adminAppointmentOverrideClient';
import AppointmentAssigneeWorkflowSection from './AppointmentAssigneeWorkflowSection';
import { useTeamMemberOperationsRealtime } from './useTeamMemberOperationsRealtime';
import UserDocsLink from '@/components/shared/UserDocsLink';
import SmartActionButton, {
  type SmartActionButtonState,
  type SmartActionMenuItem,
} from '@/app/client-zone/modules/total-operations/components/smart-task/components/SmartActionButton';
import { getSmartTaskCurrentStatusInfo } from '@/app/client-zone/modules/total-operations/components/smart-task/utils/workItemStatusPresentation';

// ============================================================================
// LOCAL TYPES
// ============================================================================

type UnknownRecord = Record<string, unknown>;

interface TeamMemberCurrentItemSummary extends UnknownRecord {
  id?: string | null;
  item_type?: string | null;
  item_number?: string | null;
  display_number?: string | null;
  title?: string | null;
  status?: string | null;
  parent_job_number?: string | null;
  location_name?: string | null;
  location_address?: string | null;
}

interface TeamMemberCurrentOperations extends UnknownRecord {
  workflow_state?: string | null;
  workflow_state_label?: string | null;
  state_started_at?: string | null;
  travel_started_at?: string | null;
  work_started_at?: string | null;
  headline?: string | null;
  detail?: string | null;
  attendance_status?: string | null;
  attendance_status_label?: string | null;
  clocked_in_at?: string | null;
  on_break_since?: string | null;
  clock_in_location?: string | null;
  current_vehicle_registration?: string | null;
  current_vehicle_id?: string | null;
  inspection_required?: boolean | null;
  inspection_overridden?: boolean | null;
  inspection_override_reason?: string | null;
  inspection_override_by_name?: string | null;
  inspection_override_at?: string | null;
  inspection_override_source?: string | null;
  inspection_override_consumed_at?: string | null;
  current_item?: TeamMemberCurrentItemSummary | null;
  active_item?: TeamMemberCurrentItemSummary | null;
  travel_target_item?: TeamMemberCurrentItemSummary | null;
  travel?: {
    from_label?: string | null;
    to_label?: string | null;
    estimated_distance_km?: number | null;
    estimated_eta_minutes?: number | null;
    started_at?: string | null;
  } | null;
  last_known_location?: {
    address?: string | null;
    updated_at?: string | null;
  } | null;
}

interface TodayAppointment {
  id: string;
  item_number?: string | null;
  display_number?: string | null;
  title: string;
  status: string;
  priority?: string | null;
  scheduled_start?: string | null;
  scheduled_end?: string | null;
  location_name?: string | null;
  location_address?: string | null;
  contact_name?: string | null;
  estimated_duration_minutes?: number | null;
}

type TeamMemberSmartStatus =
  | 'scheduled'
  | 'on_route'
  | 'on_site'
  | 'in_progress'
  | 'paused'
  | 'resolved'
  | 'rescheduled';

type TeamMemberCurrentCondition =
  | TeamMemberSmartStatus
  | 'vehicle_required'
  | 'inspection_pending'
  | 'travel_paused'
  | 'completing'
  | 'idle';

const TEAM_MEMBER_SMART_ACTIONS: Array<{
  value: TeamMemberSmartStatus;
  label: string;
  icon: React.ElementType;
  tone: NonNullable<SmartActionMenuItem['tone']>;
}> = [
    { value: 'scheduled', label: 'Reset to scheduled', icon: RotateCcw, tone: 'slate' },
    { value: 'on_route', label: 'Set on route', icon: Navigation, tone: 'blue' },
    { value: 'on_site', label: 'Set on site', icon: MapPin, tone: 'cyan' },
    { value: 'in_progress', label: 'Start in progress', icon: PlayCircle, tone: 'emerald' },
    { value: 'paused', label: 'Pause in progress', icon: PauseCircle, tone: 'amber' },
    { value: 'resolved', label: 'End as resolved', icon: CheckCircle2, tone: 'emerald' },
    { value: 'rescheduled', label: 'Reschedule', icon: CalendarClock, tone: 'red' },
  ];

function isTeamMemberSmartStatus(status: string): status is TeamMemberSmartStatus {
  return TEAM_MEMBER_SMART_ACTIONS.some((action) => action.value === status);
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function isRecord(value: unknown): value is UnknownRecord {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function toNonEmptyString(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

function formatItemTypeLabel(itemType: unknown): string {
  const normalized = toNonEmptyString(itemType);
  if (!normalized) return '';
  return normalized.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatStatusLabel(status: unknown): string {
  const normalized = toNonEmptyString(status);
  if (!normalized) return '';
  return normalized.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDateTime(value: unknown): string | null {
  const normalized = toNonEmptyString(value);
  if (!normalized) return null;
  return formatDateTimeSAST(normalized, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDurationSince(value: unknown): string | null {
  const normalized = toNonEmptyString(value);
  if (!normalized) return null;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return null;
  const diffMs = Date.now() - date.getTime();
  if (!Number.isFinite(diffMs) || diffMs < 0) return null;
  const totalMinutes = Math.floor(diffMs / 60000);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function formatTimeOfDay(value: unknown): string | null {
  const normalized = toNonEmptyString(value);
  if (!normalized) return null;
  return formatTimeSAST(normalized);
}

function normalizeWorkflowState(value: unknown): string {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

function isTravelWorkflowState(value: unknown): boolean {
  const s = normalizeWorkflowState(value);
  return s === 'traveling' || s === 'travel_paused';
}

function normalizeAppointmentStatus(value: unknown): TeamMemberSmartStatus | null {
  if (typeof value !== 'string') return null;
  const s = value.trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (!s) return null;
  if (s === 'en_route') return 'on_route';
  if (s === 'arrived') return 'on_site';
  if (s === 'checked_in' || s === 'working') return 'in_progress';
  if (s === 'on_hold') return 'paused';
  if (s === 'reschedule') return 'rescheduled';
  return TEAM_MEMBER_SMART_ACTIONS.some((a) => a.value === s as TeamMemberSmartStatus)
    ? s as TeamMemberSmartStatus
    : null;
}

function mapWorkflowStateToAppointmentStatus(value: unknown): TeamMemberSmartStatus | null {
  const s = normalizeWorkflowState(value);
  switch (s) {
    case 'ready': return 'scheduled';
    case 'traveling': return 'on_route';
    case 'travel_paused': return 'on_route';
    case 'arrived': return 'on_site';
    case 'checked_in': return 'in_progress';
    case 'working': return 'in_progress';
    case 'paused': return 'paused';
    case 'completed': return 'resolved';
    case 'cancelled': return 'rescheduled';
    default: return null;
  }
}

function mapWorkflowStateToCurrentCondition(value: unknown, label?: string | null, headline?: string | null): TeamMemberCurrentCondition | null {
  const s = normalizeWorkflowState(value);
  const normalizedLabel = typeof label === 'string' ? label.trim().toLowerCase() : '';
  const normalizedHeadline = typeof headline === 'string' ? headline.trim().toLowerCase() : '';

  // First check the workflow state
  switch (s) {
    case 'idle':
    case 'vehicle_required':
    case 'inspection_pending':
    case 'travel_paused':
    case 'completing':
      return s;
    case 'ready':
      return 'scheduled';
    case 'traveling':
      return 'on_route';
    case 'arrived':
      return 'on_site';
    case 'checked_in':
    case 'working':
      return 'in_progress';
    case 'paused':
      return 'paused';
    case 'completed':
      return 'resolved';
    case 'cancelled':
      return 'rescheduled';
  }

  // Fallback: check label and headline for vehicle inspection indicators
  if (normalizedLabel.includes('vehicle') && (normalizedLabel.includes('required') || normalizedLabel.includes('needed'))) {
    return 'vehicle_required';
  }
  if (normalizedLabel.includes('inspection') && (normalizedLabel.includes('required') || normalizedLabel.includes('pending') || normalizedLabel.includes('needed'))) {
    return 'inspection_pending';
  }
  if (normalizedHeadline.includes('vehicle') && (normalizedHeadline.includes('required') || normalizedHeadline.includes('needed'))) {
    return 'vehicle_required';
  }
  if (normalizedHeadline.includes('inspection') && (normalizedHeadline.includes('required') || normalizedHeadline.includes('pending') || normalizedHeadline.includes('needed'))) {
    return 'inspection_pending';
  }
  if (normalizedHeadline.includes('take on vehicle') || normalizedHeadline.includes('take-on vehicle')) {
    return 'vehicle_required';
  }

  return null;
}

function formatSmartTaskStatusLabel(value: TeamMemberSmartStatus | null): string {
  if (!value) return 'Unknown status';
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatSmartTaskConditionLabel(
  value: TeamMemberCurrentCondition | null,
  options?: {
    attendanceStatus?: unknown;
  },
): string {
  switch (value) {
    case 'vehicle_required':
      return 'Vehicle Required';
    case 'inspection_pending':
      return 'Inspection Required';
    case 'travel_paused':
      return 'Travel Paused';
    case 'completing':
      return 'Completing';
    case 'idle': {
      const attendanceStatus = normalizeWorkflowState(options?.attendanceStatus);
      if (attendanceStatus === 'on_break') {
        return 'On Break';
      }
      if (attendanceStatus === 'clocked_in') {
        return 'Available';
      }
      return 'Idle';
    }
    default:
      return formatSmartTaskStatusLabel(value);
  }
}

function getSmartTaskActionButtonState(value: TeamMemberCurrentCondition | null): SmartActionButtonState {
  if (value === 'paused') return 'paused';
  if (value === 'scheduled' || value === 'on_site') return 'default';
  return 'default';
}

function getSmartTaskStatusRibbon(
  value: TeamMemberCurrentCondition | null,
  isDark: boolean,
  options?: {
    currentVehicleRegistration?: string | null;
    attendanceStatus?: unknown;
  },
) {
  switch (value) {
    case 'vehicle_required':
      return {
        icon: Car,
        label: 'Vehicle Required',
        detail: 'A vehicle still needs to be selected before travel or work can begin.',
        backgroundClassName: isDark ? 'bg-rose-950/50' : 'bg-rose-50',
        borderClassName: isDark ? 'border-rose-900/60' : 'border-rose-200',
        textClassName: isDark ? 'text-rose-300' : 'text-rose-700',
        detailClassName: isDark ? 'text-rose-200/80' : 'text-rose-800',
      };
    case 'inspection_pending': {
      const vehicleRegistration = toNonEmptyString(options?.currentVehicleRegistration);
      return {
        icon: AlertTriangle,
        label: 'Inspection Required',
        detail: vehicleRegistration
          ? `${vehicleRegistration} still needs its take-on inspection before this appointment can start.`
          : 'A take-on vehicle inspection is still outstanding before this appointment can start.',
        backgroundClassName: isDark ? 'bg-amber-950/50' : 'bg-amber-50',
        borderClassName: isDark ? 'border-amber-900/60' : 'border-amber-200',
        textClassName: isDark ? 'text-amber-300' : 'text-amber-700',
        detailClassName: isDark ? 'text-amber-200/80' : 'text-amber-800',
      };
    }
    case 'on_route':
      return {
        icon: Route,
        label: 'En Route',
        detail: 'Team member is currently travelling to a site',
        backgroundClassName: isDark ? 'bg-blue-900/30' : 'bg-blue-50',
        borderClassName: isDark ? 'border-blue-800' : 'border-blue-200',
        textClassName: isDark ? 'text-blue-300' : 'text-blue-700',
        detailClassName: isDark ? 'text-blue-400' : 'text-blue-600',
      };
    case 'on_site':
      return {
        icon: MapPin,
        label: 'On Site',
        detail: 'Team member has arrived at the site',
        backgroundClassName: isDark ? 'bg-cyan-900/30' : 'bg-cyan-50',
        borderClassName: isDark ? 'border-cyan-800' : 'border-cyan-200',
        textClassName: isDark ? 'text-cyan-300' : 'text-cyan-700',
        detailClassName: isDark ? 'text-cyan-400' : 'text-cyan-600',
      };
    case 'travel_paused':
      return {
        icon: PauseCircle,
        label: 'Travel Paused',
        detail: 'Travel is paused and needs to be resumed before arrival can be recorded.',
        backgroundClassName: isDark ? 'bg-indigo-950/50' : 'bg-indigo-50',
        borderClassName: isDark ? 'border-indigo-900/60' : 'border-indigo-200',
        textClassName: isDark ? 'text-indigo-300' : 'text-indigo-700',
        detailClassName: isDark ? 'text-indigo-200/80' : 'text-indigo-800',
      };
    case 'in_progress':
      return {
        icon: PlayCircle,
        label: 'In Progress',
        detail: 'Team member is actively working',
        backgroundClassName: isDark ? 'bg-emerald-900/30' : 'bg-emerald-50',
        borderClassName: isDark ? 'border-emerald-800' : 'border-emerald-200',
        textClassName: isDark ? 'text-emerald-300' : 'text-emerald-700',
        detailClassName: isDark ? 'text-emerald-400' : 'text-emerald-600',
      };
    case 'paused':
      return {
        icon: PauseCircle,
        label: 'Paused',
        detail: 'Work is currently paused',
        backgroundClassName: isDark ? 'bg-amber-900/30' : 'bg-amber-50',
        borderClassName: isDark ? 'border-amber-800' : 'border-amber-200',
        textClassName: isDark ? 'text-amber-300' : 'text-amber-700',
        detailClassName: isDark ? 'text-amber-400' : 'text-amber-600',
      };
    case 'completing':
      return {
        icon: CheckCircle2,
        label: 'Completing',
        detail: 'Final close-out steps are still being completed for this appointment.',
        backgroundClassName: isDark ? 'bg-lime-950/50' : 'bg-lime-50',
        borderClassName: isDark ? 'border-lime-900/60' : 'border-lime-200',
        textClassName: isDark ? 'text-lime-300' : 'text-lime-700',
        detailClassName: isDark ? 'text-lime-200/80' : 'text-lime-800',
      };
    case 'resolved':
      return {
        icon: CheckCircle2,
        label: 'Resolved',
        detail: 'Work has been completed',
        backgroundClassName: isDark ? 'bg-green-900/30' : 'bg-green-50',
        borderClassName: isDark ? 'border-green-800' : 'border-green-200',
        textClassName: isDark ? 'text-green-300' : 'text-green-700',
        detailClassName: isDark ? 'text-green-400' : 'text-green-600',
      };
    case 'rescheduled':
      return {
        icon: CalendarClock,
        label: 'Rescheduled',
        detail: 'Appointment has been rescheduled',
        backgroundClassName: isDark ? 'bg-red-900/30' : 'bg-red-50',
        borderClassName: isDark ? 'border-red-800' : 'border-red-200',
        textClassName: isDark ? 'text-red-300' : 'text-red-700',
        detailClassName: isDark ? 'text-red-400' : 'text-red-600',
      };
    case 'idle': {
      const attendanceStatus = normalizeWorkflowState(options?.attendanceStatus);
      const isClockedIn = attendanceStatus === 'clocked_in';
      const isOnBreak = attendanceStatus === 'on_break';

      return {
        icon: Clock,
        label: isOnBreak ? 'On Break' : isClockedIn ? 'Available' : 'Idle',
        detail: isOnBreak
          ? 'Team member is clocked in but currently on break.'
          : isClockedIn
            ? 'Team member is clocked in and waiting for the next SmartTask action.'
            : 'Waiting to begin — no active SmartTask yet',
        backgroundClassName: isDark ? 'bg-gray-800' : 'bg-gray-50',
        borderClassName: isDark ? 'border-gray-700' : 'border-gray-200',
        textClassName: isDark ? 'text-gray-300' : 'text-gray-600',
        detailClassName: isDark ? 'text-gray-400' : 'text-gray-500',
      };
    }
    default:
      return {
        icon: Clock,
        label: 'Scheduled',
        detail: 'Waiting to begin — no active state yet',
        backgroundClassName: isDark ? 'bg-gray-800' : 'bg-gray-50',
        borderClassName: isDark ? 'border-gray-700' : 'border-gray-200',
        textClassName: isDark ? 'text-gray-300' : 'text-gray-600',
        detailClassName: isDark ? 'text-gray-400' : 'text-gray-500',
      };
  }
}

function parseCustomFields(value: unknown): UnknownRecord {
  if (!value) return {};
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return isRecord(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  return isRecord(value) ? value : {};
}

// ============================================================================
// STATUS COLOUR HELPERS FOR APPOINTMENT LIST
// ============================================================================

function getAppointmentStatusColors(status: string) {
  const map: Record<string, { badge: string; border: string }> = {
    pending: { badge: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300', border: 'border-l-gray-400' },
    scheduled: { badge: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300', border: 'border-l-blue-400' },
    dispatched: { badge: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300', border: 'border-l-indigo-400' },
    en_route: { badge: 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300', border: 'border-l-purple-400' },
    arrived: { badge: 'bg-cyan-50 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300', border: 'border-l-cyan-400' },
    in_progress: { badge: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300', border: 'border-l-amber-400' },
    completed: { badge: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300', border: 'border-l-green-500' },
    cancelled: { badge: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300', border: 'border-l-red-400' },
  };
  return map[status] || map.pending;
}

// ============================================================================
// PROPS
// ============================================================================

interface TeamMemberSmartTaskTabProps {
  workItem: WorkItem;
  userId: number;
  isDark: boolean;
  refreshKey?: number;
  isActive?: boolean;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function TeamMemberSmartTaskTab({
  workItem,
  userId,
  isDark,
  refreshKey = 0,
  isActive,
}: TeamMemberSmartTaskTabProps) {
  const cf = useMemo(() => parseCustomFields(workItem.custom_fields), [workItem.custom_fields]);

  // Data state
  const [liveWorkItem, setLiveWorkItem] = useState<WorkItem | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [todayAppointments, setTodayAppointments] = useState<TodayAppointment[]>([]);
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);
  const [smartActionMenuOpen, setSmartActionMenuOpen] = useState(false);
  const isActiveTab = isActive ?? true;
  const groupedRealtimeRefreshTimeoutRef = useRef<number | null>(null);
  const inFlightProfileRefreshRef = useRef<Promise<void> | null>(null);
  const inFlightAppointmentsRefreshRef = useRef<Promise<void> | null>(null);

  // ── Live profile fetch ──────────────────────────────────────────────────
  const refreshLiveProfile = useCallback(async () => {
    if (!isActiveTab) return;
    if (inFlightProfileRefreshRef.current) {
      return inFlightProfileRefreshRef.current;
    }

    const runRefresh = async () => {
      const token = authService.getTokens()?.access_token;
      if (!token) return;
      setIsRefreshing(true);
      try {
        const response = await fetch(`/api/v1/team/members/${userId}/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error(`Profile fetch failed (${response.status})`);
        const payload = await response.json();
        if (payload?.success && payload?.data) {
          setLiveWorkItem(payload.data as WorkItem);
        }
      } catch (err) {
        console.error('[TeamMemberSmartTaskTab] Profile refresh error:', err);
      } finally {
        setIsRefreshing(false);
      }
    };

    const refreshPromise = runRefresh().finally(() => {
      if (inFlightProfileRefreshRef.current === refreshPromise) {
        inFlightProfileRefreshRef.current = null;
      }
    });

    inFlightProfileRefreshRef.current = refreshPromise;
    return refreshPromise;
  }, [isActiveTab, userId]);

  // ── Today's appointments fetch ─────────────────────────────────────────
  const fetchTodayAppointments = useCallback(async () => {
    if (!isActiveTab) return;
    if (inFlightAppointmentsRefreshRef.current) {
      return inFlightAppointmentsRefreshRef.current;
    }

    const runFetch = async () => {
      const token = authService.getTokens()?.access_token;
      if (!token) return;
      setAppointmentsLoading(true);
      try {
        const now = new Date();
        const startOfDay = new Date(now);
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(now);
        endOfDay.setHours(23, 59, 59, 999);

        const params = new URLSearchParams({
          item_type: 'appointment',
          assigned_to: String(userId),
          scheduled_start_from: startOfDay.toISOString(),
          scheduled_start_to: endOfDay.toISOString(),
          sort_by: 'scheduled_start',
          sort_order: 'asc',
          limit: '50',
        });

        const response = await fetch(`/api/v1/work-items?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) return;
        const payload = await response.json();
        if (payload?.success && Array.isArray(payload?.data)) {
          setTodayAppointments(
            payload.data.flatMap((item: unknown) => {
              if (!isRecord(item)) {
                return [];
              }

              const customFields = parseCustomFields(item.custom_fields);
              const appointmentId = toNonEmptyString(item.id);

              if (!appointmentId) {
                return [];
              }

              return [{
                id: appointmentId,
                item_number: toNonEmptyString(item.item_number),
                display_number: toNonEmptyString(item.display_number) || toNonEmptyString(item.item_number),
                title: toNonEmptyString(item.title) || 'Untitled appointment',
                status: toNonEmptyString(item.status) || 'scheduled',
                priority: toNonEmptyString(item.priority),
                scheduled_start: toNonEmptyString(item.scheduled_start),
                scheduled_end: toNonEmptyString(item.scheduled_end),
                location_name: toNonEmptyString(item.location_name) || toNonEmptyString(customFields.location_name),
                location_address: toNonEmptyString(item.location_address) || toNonEmptyString(customFields.location_address),
                contact_name: toNonEmptyString(item.contact_name),
                estimated_duration_minutes: typeof item.estimated_duration_minutes === 'number'
                  ? item.estimated_duration_minutes
                  : null,
              }];
            }),
          );
        }
      } catch (err) {
        console.error('[TeamMemberSmartTaskTab] Appointments fetch error:', err);
      } finally {
        setAppointmentsLoading(false);
      }
    };

    const fetchPromise = runFetch().finally(() => {
      if (inFlightAppointmentsRefreshRef.current === fetchPromise) {
        inFlightAppointmentsRefreshRef.current = null;
      }
    });

    inFlightAppointmentsRefreshRef.current = fetchPromise;
    return fetchPromise;
  }, [isActiveTab, userId]);

  const runRealtimeRefresh = useCallback(() => {
    if (!isActiveTab) {
      return;
    }

    void Promise.allSettled([
      refreshLiveProfile(),
      fetchTodayAppointments(),
    ]);
  }, [fetchTodayAppointments, isActiveTab, refreshLiveProfile]);

  const scheduleRealtimeRefresh = useCallback(() => {
    if (!isActiveTab || typeof window === 'undefined') {
      return;
    }

    if (groupedRealtimeRefreshTimeoutRef.current) {
      window.clearTimeout(groupedRealtimeRefreshTimeoutRef.current);
    }

    groupedRealtimeRefreshTimeoutRef.current = window.setTimeout(() => {
      groupedRealtimeRefreshTimeoutRef.current = null;
      runRealtimeRefresh();
    }, 120);
  }, [isActiveTab, runRealtimeRefresh]);

  // ── Initial load ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!isActiveTab) {
      return;
    }

    runRealtimeRefresh();
  }, [isActiveTab, refreshKey, runRealtimeRefresh]);

  useTeamMemberOperationsRealtime({
    teamMemberUserId: userId,
    enabled: isActiveTab,
    onRelevantMessage: scheduleRealtimeRefresh,
  });

  useEffect(() => {
    return () => {
      if (typeof window !== 'undefined' && groupedRealtimeRefreshTimeoutRef.current) {
        window.clearTimeout(groupedRealtimeRefreshTimeoutRef.current);
        groupedRealtimeRefreshTimeoutRef.current = null;
      }
    };
  }, []);

  // ── Derived state from live profile ───────────────────────────────────
  const effectiveWorkItem = liveWorkItem ?? workItem;
  const liveCf = useMemo(
    () => parseCustomFields(effectiveWorkItem.custom_fields),
    [effectiveWorkItem.custom_fields],
  );

  const currentOperations = useMemo(() => {
    const value = liveCf.current_operations;
    return isRecord(value) ? value as TeamMemberCurrentOperations : null;
  }, [liveCf.current_operations]);

  const currentItem = isRecord(currentOperations?.current_item)
    ? currentOperations?.current_item as TeamMemberCurrentItemSummary
    : null;

  const statusActiveItem = useMemo(() => {
    const candidates = [
      currentOperations?.current_item,
      currentOperations?.active_item,
      currentOperations?.travel_target_item,
    ];

    const candidate = candidates.find((item) => (
      isRecord(item)
      && (
        toNonEmptyString((item as TeamMemberCurrentItemSummary).id)
        || toNonEmptyString((item as TeamMemberCurrentItemSummary).item_number)
        || toNonEmptyString((item as TeamMemberCurrentItemSummary).display_number)
        || toNonEmptyString((item as TeamMemberCurrentItemSummary).title)
      )
    ));

    return candidate && isRecord(candidate) ? candidate as TeamMemberCurrentItemSummary : null;
  }, [currentOperations?.active_item, currentOperations?.current_item, currentOperations?.travel_target_item]);

  const statusTravelItem = useMemo(() => {
    const candidates = [
      currentOperations?.travel_target_item,
      currentOperations?.current_item,
      currentOperations?.active_item,
    ];

    const candidate = candidates.find((item) => (
      isRecord(item)
      && (
        toNonEmptyString((item as TeamMemberCurrentItemSummary).id)
        || toNonEmptyString((item as TeamMemberCurrentItemSummary).item_number)
        || toNonEmptyString((item as TeamMemberCurrentItemSummary).display_number)
        || toNonEmptyString((item as TeamMemberCurrentItemSummary).title)
      )
    ));

    return candidate && isRecord(candidate) ? candidate as TeamMemberCurrentItemSummary : null;
  }, [currentOperations?.active_item, currentOperations?.current_item, currentOperations?.travel_target_item]);

  const normalizedWorkflowState = normalizeWorkflowState(currentOperations?.workflow_state);
  const showTravelContext = isTravelWorkflowState(normalizedWorkflowState);

  const travel = currentOperations?.travel && isRecord(currentOperations.travel)
    ? currentOperations.travel
    : null;

  const lastKnownLocation = currentOperations?.last_known_location && isRecord(currentOperations.last_known_location)
    ? currentOperations.last_known_location
    : null;

  const currentItemDisplayNumber = toNonEmptyString(currentItem?.display_number)
    || toNonEmptyString(currentItem?.item_number);

  const currentItemLocation = toNonEmptyString(currentItem?.location_name)
    || toNonEmptyString(currentItem?.location_address);

  const currentItemReference = [
    currentItemDisplayNumber,
    currentItem?.parent_job_number && currentItem?.item_type === 'job'
      ? null
      : toNonEmptyString(currentItem?.parent_job_number),
  ].filter((v): v is string => Boolean(v)).join(' · ');

  const stateStartedAt = toNonEmptyString(currentOperations?.state_started_at)
    || toNonEmptyString(currentOperations?.travel_started_at)
    || toNonEmptyString(currentOperations?.work_started_at);

  const activeFor = formatDurationSince(stateStartedAt);
  const stateStartedDisplay = formatDateTime(stateStartedAt);
  const clockedInAtDisplay = formatDateTime(currentOperations?.clocked_in_at);

  // ── Controllable appointment ───────────────────────────────────────────
  const controllableAppointment = useMemo(() => {
    const candidate = [
      currentOperations?.current_item,
      currentOperations?.active_item,
      currentOperations?.travel_target_item,
    ].find(
      (item) =>
        isRecord(item) &&
        toNonEmptyString((item as TeamMemberCurrentItemSummary).item_type) === 'appointment' &&
        toNonEmptyString((item as TeamMemberCurrentItemSummary).id),
    );
    return candidate && isRecord(candidate) ? candidate as TeamMemberCurrentItemSummary : null;
  }, [currentOperations?.active_item, currentOperations?.current_item, currentOperations?.travel_target_item]);

  const appointmentWorkflowContext = useMemo(() => {
    if (!controllableAppointment?.id) return null;
    const [firstName, ...restName] = (effectiveWorkItem.title || '').trim().split(/\s+/).filter(Boolean);
    const lastName = restName.join(' ');
    const teamMemberEmail = toNonEmptyString(cf.email);
    return {
      id: controllableAppointment.id as string,
      status: controllableAppointment.status || undefined,
      assigned_to: userId,
      assigned_to_name: effectiveWorkItem.title || teamMemberEmail || `User ${userId}`,
      assignee_name: effectiveWorkItem.title || teamMemberEmail || `User ${userId}`,
      custom_fields: {},
      assignees: [
        {
          user_id: userId,
          first_name: firstName || null,
          last_name: lastName || null,
          email: teamMemberEmail,
        },
      ],
    };
  }, [cf.email, controllableAppointment?.id, controllableAppointment?.status, effectiveWorkItem.title, userId]);

  const controlledAppointmentStatus = useMemo(
    () =>
      normalizeAppointmentStatus(appointmentWorkflowContext?.status)
      || normalizeAppointmentStatus(controllableAppointment?.status)
      || mapWorkflowStateToAppointmentStatus(currentOperations?.workflow_state)
      || null,
    [appointmentWorkflowContext?.status, controllableAppointment?.status, currentOperations?.workflow_state],
  );

  const smartTaskCurrentCondition = useMemo<TeamMemberCurrentCondition | null>(() => {
    const condition = mapWorkflowStateToCurrentCondition(
      currentOperations?.workflow_state,
      currentOperations?.workflow_state_label,
      currentOperations?.headline
    ) || controlledAppointmentStatus;
    // Server-computed inspection_required mirrors the driver's enforcement
    // gate — a passive state (idle/scheduled) with an unsatisfied inspection
    // rule shows "Inspection Required" just like inspection_pending does.
    if (
      currentOperations?.inspection_required === true
      && currentOperations?.inspection_overridden !== true
      && (condition === 'idle' || condition === 'scheduled' || condition == null)
    ) {
      return 'inspection_pending';
    }
    return condition;
  }, [controlledAppointmentStatus, currentOperations?.workflow_state, currentOperations?.workflow_state_label, currentOperations?.headline, currentOperations?.inspection_required, currentOperations?.inspection_overridden]);

  const smartTaskCurrentConditionLabel = useMemo(
    () => formatSmartTaskConditionLabel(smartTaskCurrentCondition, { attendanceStatus: currentOperations?.attendance_status }),
    [currentOperations?.attendance_status, smartTaskCurrentCondition],
  );

  const smartTaskRibbon = useMemo(
    () => getSmartTaskStatusRibbon(smartTaskCurrentCondition, isDark, {
      currentVehicleRegistration: currentOperations?.current_vehicle_registration,
      attendanceStatus: currentOperations?.attendance_status,
    }),
    [currentOperations?.attendance_status, currentOperations?.current_vehicle_registration, isDark, smartTaskCurrentCondition],
  );

  const smartTaskActionButtonState = useMemo(
    () => getSmartTaskActionButtonState(smartTaskCurrentCondition),
    [smartTaskCurrentCondition],
  );

  const currentStatusDisplay = useMemo(() => getSmartTaskCurrentStatusInfo({
    currentState: currentOperations?.workflow_state,
    activeTarget: statusActiveItem ? {
      item_type: toNonEmptyString(statusActiveItem.item_type),
      item_subtype: null,
      item_number: toNonEmptyString(statusActiveItem.item_number),
      display_number: toNonEmptyString(statusActiveItem.display_number) || toNonEmptyString(statusActiveItem.item_number),
      title: toNonEmptyString(statusActiveItem.title),
    } : null,
    travelTarget: statusTravelItem ? {
      item_type: toNonEmptyString(statusTravelItem.item_type),
      item_subtype: null,
      item_number: toNonEmptyString(statusTravelItem.item_number),
      display_number: toNonEmptyString(statusTravelItem.display_number) || toNonEmptyString(statusTravelItem.item_number),
      title: toNonEmptyString(statusTravelItem.title),
    } : null,
  }), [currentOperations?.workflow_state, statusActiveItem, statusTravelItem]);

  const smartTaskControlStatus = currentStatusDisplay.label;

  const smartTaskControlAssignment = controllableAppointment
    ? `${formatItemTypeLabel(controllableAppointment.item_type)} · ${toNonEmptyString(controllableAppointment.display_number)
    || toNonEmptyString(controllableAppointment.item_number)
    || 'Live item'
    }`
    : 'No live appointment selected';

  const smartTaskControlOverrideState = appointmentWorkflowContext?.status
    ? formatStatusLabel(appointmentWorkflowContext.status)
    : 'Waiting for live appointment context';
  const allowedSmartTaskOverrideStatuses = useMemo<TeamMemberSmartStatus[]>(
    () => getAllowedAppointmentAdminOverrideStatuses(
      appointmentWorkflowContext?.status
      || controllableAppointment?.status
      || currentOperations?.workflow_state,
    ).filter((status): status is TeamMemberSmartStatus => isTeamMemberSmartStatus(status)),
    [appointmentWorkflowContext?.status, controllableAppointment?.status, currentOperations?.workflow_state],
  );

  // ── Workflow override ──────────────────────────────────────────────────
  const handleSmartTaskOverride = useCallback(
    async (requestedStatus: TeamMemberSmartStatus) => {
      if (!appointmentWorkflowContext?.id) return;

      const currentAppointmentStatus = appointmentWorkflowContext?.status
        || controllableAppointment?.status
        || currentOperations?.workflow_state
        || null;

      const token = authService.getTokens()?.access_token;
      if (!token) {
        notify.error('Authentication expired. Please sign in again.');
        return;
      }
      try {
        const preparedOverride = await prepareAdminAppointmentOverride({
          authToken: token,
          workItemId: appointmentWorkflowContext.id,
          requestedStatus,
          currentStatus: currentAppointmentStatus,
          subjectLabel: effectiveWorkItem.title || `User ${userId}`,
          targetUserId: userId,
        });

        if (!preparedOverride) {
          return;
        }

        const response = await fetch('/api/v1/work-items/workflow-state', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(preparedOverride.requestBody),
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok || !payload?.success) {
          throw new Error(payload?.error || 'Failed to update SmartTask status');
        }
        notify.success(formatAdminAppointmentOverrideSuccessMessage({
          requestedStatus,
          successContext: preparedOverride.successContext,
        }));
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('workItemStatusChange', {
            detail: { workItemId: appointmentWorkflowContext.id },
          }));
          window.dispatchEvent(new CustomEvent('workItemTimeEntriesChanged', {
            detail: { workItemId: appointmentWorkflowContext.id },
          }));
        }
        runRealtimeRefresh();
      } catch (err) {
        notify.error(err instanceof Error ? err.message : 'Failed to update SmartTask status');
      }
    },
    [appointmentWorkflowContext?.id, appointmentWorkflowContext?.status, controllableAppointment?.status, currentOperations?.workflow_state, effectiveWorkItem.title, runRealtimeRefresh, userId],
  );

  const [vehicleOverrideBusy, setVehicleOverrideBusy] = useState(false);
  const [showInspectionOverrideDialog, setShowInspectionOverrideDialog] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [overrideNotes, setOverrideNotes] = useState('');

  const handleVehicleOverride = useCallback(async () => {
    if (!overrideReason.trim()) {
      notify.error('Please provide a reason for the inspection override');
      return;
    }

    setVehicleOverrideBusy(true);
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) {
        notify.error('Authentication expired. Please sign in again.');
        return;
      }
      const response = await fetch('/api/v1/work-items/workflow-state', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'override_inspection',
          target_user_id: userId,
          vehicle_id: currentOperations?.current_vehicle_id || null,
          vehicle_registration: currentOperations?.current_vehicle_registration || null,
          metadata: {
            target_user_id: userId,
            reason: overrideReason.trim(),
            notes: overrideNotes.trim() || undefined,
          },
        }),
      });

      if (response.status === 409) {
        const errorData = await response.json().catch(() => null);
        notify.warning(errorData?.error || 'An inspection override already exists for this driver. The existing override will be consumed on the next inspection skip.', {
          duration: 6000,
        });
        setShowInspectionOverrideDialog(false);
        setOverrideReason('');
        setOverrideNotes('');
        runRealtimeRefresh();
        return;
      }

      const data = await response.json();
      if (!data.success) {
        notify.error(data.error || 'Failed to authorize vehicle override');
        return;
      }

      notify.success(`Single-use inspection override authorized for ${effectiveWorkItem.title || `User ${userId}`}. The override will be consumed when they skip the next inspection.`);
      setShowInspectionOverrideDialog(false);
      setOverrideReason('');
      setOverrideNotes('');
      runRealtimeRefresh();
    } catch (err) {
      console.error('[SmartTaskTab] Vehicle override error:', err);
      notify.error('Failed to authorize vehicle override');
    } finally {
      setVehicleOverrideBusy(false);
    }
  }, [overrideReason, overrideNotes, userId, currentOperations?.current_vehicle_id, currentOperations?.current_vehicle_registration, effectiveWorkItem.title, runRealtimeRefresh]);

  const smartTaskActionItems = useMemo<SmartActionMenuItem[]>(() => {
    if (!appointmentWorkflowContext?.id) return [];
    return TEAM_MEMBER_SMART_ACTIONS
      .filter((a) => a.value !== controlledAppointmentStatus && allowedSmartTaskOverrideStatuses.includes(a.value))
      .map((a) => ({
        key: a.value,
        label: a.label,
        icon: a.icon,
        tone: a.tone,
        onClick: async () => handleSmartTaskOverride(a.value),
      }));
  }, [allowedSmartTaskOverrideStatuses, appointmentWorkflowContext?.id, controlledAppointmentStatus, handleSmartTaskOverride]);

  // ── Rendering ──────────────────────────────────────────────────────────

  const deviceTime = new Date().toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit', hour12: false });
  const deviceWorkerName = effectiveWorkItem.title || `User ${userId}`;

  return (
    <div className="space-y-6">

      {/* ── Admin notice ── */}
      <div className={`flex items-start gap-3 rounded-xl border px-4 py-3 ${isDark ? 'bg-blue-900/20 border-blue-800 text-blue-300' : 'bg-blue-50 border-blue-200 text-blue-700'}`}>
        <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
        <p className="text-xs leading-relaxed">
          <span className="font-semibold">Admin remote control</span> — You are viewing and managing the live SmartTask state for{' '}
          <span className="font-semibold">{deviceWorkerName}</span>. Overrides here sync to their device in real time via WebSocket.
        </p>
        {isRefreshing && <Loader2 className="ml-auto h-4 w-4 animate-spin flex-shrink-0 text-blue-400" />}
      </div>

      {/* ── Device Frame: Field Worker's Live View ── */}
      <div className={`mx-auto w-full max-w-[420px] ${isDark ? '' : ''}`}>
        <div className="flex items-center justify-center gap-2 mb-2">
          <MonitorSmartphone className={`h-4 w-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
          <span className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            {deviceWorkerName}&apos;s device preview
          </span>
        </div>

        <div className={`overflow-hidden rounded-[2rem] border-[3px] shadow-[0_22px_65px_rgba(15,23,42,0.18)] ${isDark ? 'border-gray-600 bg-gray-900 shadow-[0_22px_65px_rgba(2,6,23,0.55)]' : 'border-gray-800 bg-white'}`}>
          {/* Device status bar */}
          <div className={`flex items-center justify-between px-6 py-2 ${isDark ? 'bg-gray-950' : 'bg-gray-900'}`}>
            <span className="text-[11px] font-semibold text-white">{deviceTime}</span>
            <div className="flex items-center gap-1.5">
              <Signal className="h-3 w-3 text-white" />
              <Wifi className="h-3 w-3 text-white" />
              <BatteryFull className="h-3.5 w-3.5 text-white" />
            </div>
          </div>

          {/* Device notch */}
          <div className={`flex justify-center ${isDark ? 'bg-gray-950' : 'bg-gray-900'}`}>
            <div className="h-[5px] w-[120px] rounded-b-full bg-gray-700" />
          </div>

          {/* Device screen content */}
          <div className={`max-h-[520px] overflow-y-auto overscroll-contain ${isDark ? 'bg-slate-900' : 'bg-gradient-to-b from-slate-50 to-white'}`}>
            {/* App header bar */}
            <div className={`sticky top-0 z-10 flex items-center justify-between px-4 py-2.5 backdrop-blur-xl ${isDark ? 'bg-slate-900/90 border-b border-slate-800' : 'bg-white/90 border-b border-gray-200'}`}>
              <div className="flex items-center gap-2">
                <div className={`flex h-7 w-7 items-center justify-center rounded-lg ${isDark ? 'bg-emerald-900/50' : 'bg-emerald-100'}`}>
                  <Zap className={`h-3.5 w-3.5 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
                </div>
                <div>
                  <p className={`text-xs font-bold leading-none ${isDark ? 'text-white' : 'text-gray-900'}`}>Smart Task</p>
                  <p className={`text-[10px] leading-tight ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{deviceWorkerName}</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <div className={`inline-flex items-center gap-1.5 rounded-full px-2 py-1 ${isDark ? 'bg-slate-800/90' : 'bg-slate-100'}`}>
                  {isRefreshing && <Loader2 className="h-3 w-3 animate-spin text-blue-400" />}
                  <span
                    className="h-1.5 w-1.5 flex-shrink-0 rounded-full"
                    style={{ backgroundColor: currentStatusDisplay.color }}
                  />
                  <span className={`max-w-[150px] truncate text-[10px] font-semibold ${isDark ? 'text-slate-100' : 'text-slate-700'}`}>
                    {currentStatusDisplay.label}
                  </span>
                </div>
              </div>
            </div>

            {/* Status ribbon inside device */}
            <div className={`px-4 py-2 ${smartTaskRibbon.backgroundClassName} ${smartTaskRibbon.borderClassName} border-b`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2">
                  <smartTaskRibbon.icon className={`h-4 w-4 flex-shrink-0 ${smartTaskRibbon.textClassName}`} />
                  <div className="min-w-0">
                    <p className={`truncate text-xs font-bold ${smartTaskRibbon.textClassName}`}>
                      {smartTaskRibbon.label}
                    </p>
                    <p className={`truncate text-[10px] ${smartTaskRibbon.detailClassName}`}>
                      {smartTaskRibbon.detail}
                    </p>
                  </div>
                </div>
                {(activeFor || stateStartedDisplay) && (
                  <span className={`flex-shrink-0 font-mono text-[11px] font-bold ${smartTaskRibbon.textClassName}`}>
                    {activeFor || ''}
                  </span>
                )}
              </div>
            </div>

            {/* Current assignment card */}
            <div className="px-4 py-3 space-y-3">
              {currentOperations ? (
                <>
                  <div className="flex flex-wrap gap-1.5">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${isDark ? 'bg-emerald-900/30 text-emerald-300' : 'bg-emerald-50 text-emerald-700'}`}>
                      <Activity className="h-2.5 w-2.5" />
                      {toNonEmptyString(currentOperations.workflow_state_label) || formatStatusLabel(currentOperations.workflow_state)}
                    </span>
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${isDark ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-50 text-blue-700'}`}>
                      <Clock className="h-2.5 w-2.5" />
                      {toNonEmptyString(currentOperations.attendance_status_label) || formatStatusLabel(currentOperations.attendance_status)}
                    </span>
                    {currentOperations.current_vehicle_registration && (
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${isDark ? 'bg-amber-900/30 text-amber-300' : 'bg-amber-50 text-amber-700'}`}>
                        <Car className="h-2.5 w-2.5" />
                        {currentOperations.current_vehicle_registration}
                      </span>
                    )}
                  </div>

                  <div className={`rounded-xl p-3 ${isDark ? 'bg-slate-800/80' : 'bg-gray-50'}`}>
                    {currentItemReference && (
                      <p className={`text-[10px] font-mono font-bold ${isDark ? 'text-orange-300' : 'text-orange-700'}`}>
                        {formatItemTypeLabel(currentItem?.item_type)} · {currentItemReference}
                      </p>
                    )}
                    <p className={`mt-0.5 text-sm font-semibold leading-snug ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {toNonEmptyString(currentOperations.headline) || currentItem?.title || 'No active workflow'}
                    </p>
                    {toNonEmptyString(currentOperations.detail) && (
                      <p className={`mt-0.5 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {currentOperations.detail}
                      </p>
                    )}
                    {currentItemLocation && (
                      <p className={`mt-1.5 flex items-start gap-1 text-[11px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        <MapPin className="mt-0.5 h-3 w-3 flex-shrink-0" />
                        <span>{currentItemLocation}</span>
                      </p>
                    )}
                  </div>

                  {showTravelContext && (toNonEmptyString(travel?.from_label) || toNonEmptyString(travel?.to_label)) && (
                    <div className="grid grid-cols-2 gap-2">
                      {toNonEmptyString(travel?.from_label) && (
                        <div className={`rounded-lg px-2.5 py-2 ${isDark ? 'bg-slate-800/60' : 'bg-white border border-gray-100'}`}>
                          <p className={`text-[9px] font-bold uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>From</p>
                          <p className={`mt-0.5 text-[11px] font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{travel?.from_label}</p>
                        </div>
                      )}
                      {toNonEmptyString(travel?.to_label) && (
                        <div className={`rounded-lg px-2.5 py-2 ${isDark ? 'bg-slate-800/60' : 'bg-white border border-gray-100'}`}>
                          <p className={`text-[9px] font-bold uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>To</p>
                          <p className={`mt-0.5 text-[11px] font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{travel?.to_label}</p>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-2">
                    {(activeFor || stateStartedDisplay) && (
                      <div className={`rounded-lg px-2.5 py-2 ${isDark ? 'bg-slate-800/60' : 'bg-white border border-gray-100'}`}>
                        <p className={`text-[9px] font-bold uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>State duration</p>
                        <p className={`mt-0.5 text-xs font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {activeFor ? `${activeFor} active` : 'Active'}
                        </p>
                      </div>
                    )}
                    {(clockedInAtDisplay || toNonEmptyString(currentOperations.clock_in_location)) && (
                      <div className={`rounded-lg px-2.5 py-2 ${isDark ? 'bg-slate-800/60' : 'bg-white border border-gray-100'}`}>
                        <p className={`text-[9px] font-bold uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Attendance</p>
                        <p className={`mt-0.5 text-xs font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {toNonEmptyString(currentOperations.attendance_status_label) || formatStatusLabel(currentOperations.attendance_status)}
                        </p>
                      </div>
                    )}
                    {showTravelContext && travel?.estimated_eta_minutes && (
                      <div className={`rounded-lg px-2.5 py-2 ${isDark ? 'bg-slate-800/60' : 'bg-white border border-gray-100'}`}>
                        <p className={`text-[9px] font-bold uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>ETA</p>
                        <p className={`mt-0.5 text-xs font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {travel.estimated_eta_minutes} min
                        </p>
                      </div>
                    )}
                    {toNonEmptyString(lastKnownLocation?.address) && (
                      <div className={`rounded-lg px-2.5 py-2 ${isDark ? 'bg-slate-800/60' : 'bg-white border border-gray-100'}`}>
                        <p className={`text-[9px] font-bold uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Location</p>
                        <p className={`mt-0.5 truncate text-[11px] font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                          {lastKnownLocation?.address}
                        </p>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className={`rounded-xl p-4 text-center ${isDark ? 'bg-slate-800/40' : 'bg-gray-50'}`}>
                  <Navigation className={`mx-auto h-8 w-8 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
                  <p className={`mt-2 text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    No active SmartTask
                  </p>
                  <p className={`mt-0.5 text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Waiting for workflow activity
                  </p>
                </div>
              )}
            </div>

            {/* Device bottom safe area */}
            <div className={`h-5 ${isDark ? 'bg-slate-900' : 'bg-white'}`}>
              <div className="mx-auto mt-2 h-[4px] w-[100px] rounded-full bg-gray-400/30" />
            </div>
          </div>
        </div>
      </div>

      {/* ── SmartTask Control Surface ── */}
      <div className={`rounded-xl border p-4 shadow-sm ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="flex items-start gap-2">
          <Zap className="mt-0.5 h-4 w-4 text-orange-500" />
          <div>
            <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
              Remote SmartTask controls
              <UserDocsLink docsPath="/total-operations/workflows/service-operations/team-member-detail#smart-task" label="User guide: Smart Task controls" className="ml-1 align-middle" />
            </h3>
            <p className={`mt-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Override the field worker&apos;s appointment workflow state remotely. Changes propagate via WebSocket in real time.
            </p>
          </div>
        </div>

        <div className={`mt-4 overflow-hidden rounded-xl border ${isDark ? 'border-gray-700' : 'border-gray-200 bg-white'}`}>
          <div className={`flex flex-col gap-3 px-4 py-3 ${isDark ? 'bg-gray-800/20' : 'bg-white'}`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <Activity className="h-4 w-4 text-emerald-500" />
                <p className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  SmartTask appointment control
                </p>
              </div>
              <h4 className={`mt-1 text-sm font-bold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                {smartTaskControlAssignment}
              </h4>
              <div className={`mt-0.5 flex items-center gap-2 flex-wrap text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                <span className="font-mono">USR{userId}</span>
                {currentItemDisplayNumber && <span>{currentItemDisplayNumber}</span>}
                {controllableAppointment?.item_type && (
                  <span>{formatItemTypeLabel(controllableAppointment.item_type)}</span>
                )}
              </div>
              <p className={`mt-1.5 text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                {currentItem?.title
                  || toNonEmptyString(currentOperations?.headline)
                  || 'This team member is not currently attached to a controllable appointment.'}
              </p>
              {(currentItemLocation || toNonEmptyString(currentOperations?.detail)) && (
                <p className={`mt-1 truncate text-[11px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {currentItemLocation || toNonEmptyString(currentOperations?.detail)}
                </p>
              )}
            </div>

            {appointmentWorkflowContext && (
              <div className="w-full">
                <SmartActionButton
                  items={smartTaskActionItems}
                  label={smartTaskCurrentConditionLabel}
                  state={smartTaskActionButtonState}
                  fullWidth
                  menuPlacement="bottom"
                  open={smartActionMenuOpen}
                  onOpenChange={setSmartActionMenuOpen}
                  icon={Activity}
                />
              </div>
            )}
          </div>

          <div className={`border-y px-4 py-1.5 ${smartTaskRibbon.backgroundClassName} ${smartTaskRibbon.borderClassName}`}>
            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2.5">
                <smartTaskRibbon.icon className={`h-4 w-4 flex-shrink-0 ${smartTaskRibbon.textClassName}`} />
                <p className={`truncate text-[11px] font-bold uppercase tracking-[0.16em] ${smartTaskRibbon.textClassName}`}>
                  {smartTaskRibbon.label}
                </p>
              </div>
              {(activeFor || stateStartedDisplay) && (
                <span className={`flex-shrink-0 font-mono text-xs font-semibold ${smartTaskRibbon.textClassName}`}>
                  {activeFor ? `${activeFor} active` : stateStartedDisplay}
                </span>
              )}
            </div>
            <p className={`mt-0.5 truncate text-xs ${smartTaskRibbon.detailClassName}`}>
              {smartTaskRibbon.detail}
            </p>
          </div>

          {(smartTaskCurrentCondition === 'vehicle_required' || smartTaskCurrentCondition === 'inspection_pending') && (
            <div className={`px-4 py-3 border-b space-y-3 ${isDark ? 'bg-amber-950/20 border-gray-700' : 'bg-amber-50/50 border-gray-200'}`}>
              {/* ── Why is inspection required ── */}
              <div className="flex items-start gap-2.5">
                <ClipboardCheck className={`mt-0.5 h-4 w-4 flex-shrink-0 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
                <div className="min-w-0 flex-1">
                  <p className={`text-xs font-semibold ${isDark ? 'text-amber-200' : 'text-amber-800'}`}>
                    {smartTaskCurrentCondition === 'inspection_pending'
                      ? 'Take-on vehicle inspection required'
                      : 'Vehicle selection required'}
                  </p>
                  <p className={`mt-0.5 text-[11px] leading-relaxed ${isDark ? 'text-amber-300/80' : 'text-amber-700'}`}>
                    {smartTaskCurrentCondition === 'inspection_pending'
                      ? (toNonEmptyString(currentOperations?.current_vehicle_registration)
                        ? `${currentOperations?.current_vehicle_registration} needs a take-on inspection before ${effectiveWorkItem.title || 'this worker'} can start travel or work.`
                        : 'A take-on vehicle inspection is blocking SmartTask. The worker must complete the inspection before proceeding.')
                      : 'A vehicle must be selected before travel or work can begin.'}
                  </p>
                </div>
              </div>

              {/* ── Existing override status ── */}
              {currentOperations?.inspection_overridden && !currentOperations?.inspection_override_consumed_at && (
                <div className={`flex items-start gap-2 rounded-lg border px-3 py-2 ${isDark ? 'border-emerald-800/60 bg-emerald-950/20' : 'border-emerald-200 bg-emerald-50/60'}`}>
                  <ShieldCheck className={`mt-0.5 h-3.5 w-3.5 flex-shrink-0 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
                  <div className="min-w-0 flex-1">
                    <p className={`text-[11px] font-semibold ${isDark ? 'text-emerald-200' : 'text-emerald-800'}`}>
                      Override already authorized
                    </p>
                    <p className={`mt-0.5 text-[10px] ${isDark ? 'text-emerald-300/70' : 'text-emerald-700'}`}>
                      {toNonEmptyString(currentOperations?.inspection_override_reason)
                        ? `Reason: ${currentOperations.inspection_override_reason}`
                        : 'A single-use override is active and will be consumed on the next inspection skip.'}
                    </p>
                    {toNonEmptyString(currentOperations?.inspection_override_by_name) && (
                      <p className={`mt-0.5 text-[10px] ${isDark ? 'text-emerald-300/60' : 'text-emerald-600'}`}>
                        Authorized by {currentOperations.inspection_override_by_name}
                        {toNonEmptyString(currentOperations?.inspection_override_at)
                          ? ` · ${formatDateTime(currentOperations.inspection_override_at)}`
                          : ''}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* ── Override button / dialog ── */}
              {!showInspectionOverrideDialog ? (
                <div className="flex justify-end">
                  <button
                    onClick={() => setShowInspectionOverrideDialog(true)}
                    disabled={vehicleOverrideBusy}
                    className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold bg-amber-600 text-white hover:bg-amber-700 transition-colors shadow-sm disabled:opacity-50"
                  >
                    <ShieldAlert className="h-3.5 w-3.5" />
                    Authorize Inspection Override
                  </button>
                </div>
              ) : (
                <div className={`space-y-3 rounded-xl border p-4 ${isDark ? 'border-amber-800/60 bg-amber-950/30' : 'border-amber-300 bg-amber-50'}`}>
                  <div className="flex items-center gap-2">
                    <ShieldAlert className={`h-4 w-4 ${isDark ? 'text-amber-300' : 'text-amber-800'}`} />
                    <span className={`text-sm font-semibold ${isDark ? 'text-amber-200' : 'text-amber-900'}`}>
                      Authorize Inspection Skip for {effectiveWorkItem.title || `User ${userId}`}
                    </span>
                  </div>
                  <p className={`text-xs ${isDark ? 'text-amber-300/80' : 'text-amber-700'}`}>
                    SmartTask will show a <strong>Skip Inspection</strong> button for this team member without requiring a PIN, and the reason below will be captured in the audit trail.
                    {toNonEmptyString(currentOperations?.current_vehicle_registration) && (
                      <> The override targets <strong>{currentOperations?.current_vehicle_registration}</strong>.</>
                    )}
                  </p>
                  <div>
                    <label className={`mb-1 block text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Reason *</label>
                    <select
                      value={overrideReason}
                      onChange={(e) => setOverrideReason(e.target.value)}
                      className={`w-full rounded-lg border px-3 py-2 text-sm ${isDark ? 'border-gray-700 bg-gray-900 text-gray-100' : 'border-gray-300 bg-white text-gray-900'}`}
                    >
                      <option value="">Select a reason…</option>
                      <option value="vehicle_in_maintenance">Vehicle in maintenance</option>
                      <option value="inspection_system_offline">Inspection system offline</option>
                      <option value="driver_exemption">Driver exemption (medical/administrative)</option>
                      <option value="temporary_vehicle_swap">Temporary vehicle swap (no inspection needed)</option>
                      <option value="admin_discretion">Admin discretion</option>
                    </select>
                  </div>
                  <div>
                    <label className={`mb-1 block text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Notes (optional)</label>
                    <textarea
                      value={overrideNotes}
                      onChange={(e) => setOverrideNotes(e.target.value)}
                      placeholder="Additional context for the override…"
                      className={`w-full resize-none rounded-lg border px-3 py-2 text-sm ${isDark ? 'border-gray-700 bg-gray-900 text-gray-100 placeholder:text-gray-500' : 'border-gray-300 bg-white text-gray-900 placeholder:text-gray-400'}`}
                      rows={2}
                    />
                  </div>
                  <div className="flex items-center gap-2 pt-1">
                    <button
                      onClick={handleVehicleOverride}
                      disabled={vehicleOverrideBusy || !overrideReason.trim()}
                      className="flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
                    >
                      {vehicleOverrideBusy ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <ShieldAlert className="h-4 w-4" />
                      )}
                      Authorize Skip
                    </button>
                    <button
                      onClick={() => {
                        setShowInspectionOverrideDialog(false);
                        setOverrideReason('');
                        setOverrideNotes('');
                      }}
                      disabled={vehicleOverrideBusy}
                      className={`rounded-lg border px-4 py-2 text-sm font-medium disabled:opacity-50 ${isDark ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 px-4 py-3 sm:grid-cols-3">
            <div className={`rounded-lg px-3 py-2 ${isDark ? 'bg-gray-800/60 border border-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
              <p className={`text-[11px] font-semibold uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Live worker state</p>
              <p className={`mt-1 text-sm font-medium ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{smartTaskControlStatus}</p>
              {activeFor && (
                <p className={`mt-0.5 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{activeFor} active</p>
              )}
              {!activeFor && stateStartedDisplay && (
                <p className={`mt-0.5 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Since {stateStartedDisplay}</p>
              )}
            </div>
            <div className={`rounded-lg px-3 py-2 ${isDark ? 'bg-gray-800/60 border border-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
              <p className={`text-[11px] font-semibold uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Controlled item</p>
              <p className={`mt-1 text-sm font-medium ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{smartTaskControlAssignment}</p>
              {(currentItem?.title || toNonEmptyString(currentOperations?.headline)) && (
                <p className={`mt-0.5 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {currentItem?.title || toNonEmptyString(currentOperations?.headline)}
                </p>
              )}
            </div>
            <div className={`rounded-lg px-3 py-2 ${isDark ? 'bg-gray-800/60 border border-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
              <p className={`text-[11px] font-semibold uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Target worker</p>
              <p className={`mt-1 text-sm font-medium ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>USR{userId}</p>
              <p className={`mt-0.5 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{smartTaskControlOverrideState}</p>
            </div>
          </div>
        </div>

        {appointmentWorkflowContext ? (
          <div className="mt-4">
            <AppointmentAssigneeWorkflowSection
              workItemId={appointmentWorkflowContext.id}
              workItem={appointmentWorkflowContext}
              isActive={isActive ?? true}
              userFilterId={userId}
              heading="Detailed SmartTask markers"
              description={`The Smart Action above mirrors the appointment detail panel. These detailed timing markers remain aligned to the live workflow entries for ${effectiveWorkItem.title || 'this team member'}.`}
              emptyMessage="This team member is not currently linked to a controllable appointment workflow."
              onWorkItemPatched={() => {
                void refreshLiveProfile();
              }}
            />
          </div>
        ) : (
          <div className={`mt-4 rounded-xl border border-dashed p-4 text-sm ${isDark ? 'border-gray-700 bg-gray-900/40 text-gray-400' : 'border-gray-200 bg-gray-50 text-gray-500'}`}>
            Full appointment override controls appear here when this team member is travelling to or working on an appointment.
          </div>
        )}
      </div>

      {/* ── Today's Schedule ── */}
      <div className={`rounded-xl border p-4 shadow-sm ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-blue-500" />
            <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
              Today&apos;s Schedule
            </h3>
          </div>
          <button
            onClick={() => {
              void fetchTodayAppointments();
              void refreshLiveProfile();
            }}
            className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-medium transition-colors ${isDark
              ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
          >
            {isRefreshing || appointmentsLoading
              ? <Loader2 className="h-3 w-3 animate-spin" />
              : <Activity className="h-3 w-3" />}
            Refresh
          </button>
        </div>
        <p className={`mt-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          All appointments assigned to this team member for today.
        </p>

        {appointmentsLoading && todayAppointments.length === 0 ? (
          <div className="mt-4 flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-blue-400" />
          </div>
        ) : todayAppointments.length === 0 ? (
          <div className={`mt-4 rounded-xl border border-dashed p-6 text-center ${isDark ? 'border-gray-700 bg-gray-900/40' : 'border-gray-200 bg-gray-50'}`}>
            <Calendar className={`mx-auto h-8 w-8 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
            <p className={`mt-3 text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>No appointments today</p>
            <p className={`mt-1 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              {effectiveWorkItem.title || 'This team member'} has no scheduled appointments for today.
            </p>
          </div>
        ) : (
          <div className="mt-4 space-y-2">
            {todayAppointments.map((appt) => {
              const colors = getAppointmentStatusColors(appt.status);
              const isCurrentItem = appt.id === String(currentItem?.id || '');
              const startTime = formatTimeOfDay(appt.scheduled_start);
              const endTime = formatTimeOfDay(appt.scheduled_end);
              return (
                <div
                  key={appt.id}
                  className={`rounded-xl border-l-4 border border-l-4 p-3 transition-colors ${colors.border} ${isCurrentItem
                    ? isDark
                      ? 'border-gray-600 bg-orange-900/20 ring-1 ring-orange-700'
                      : 'border-gray-200 bg-orange-50 ring-1 ring-orange-200'
                    : isDark
                      ? 'border-gray-700 bg-gray-900/40'
                      : 'border-gray-200 bg-gray-50'
                    }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        {appt.display_number && (
                          <span className={`font-mono text-[11px] font-semibold ${isDark ? 'text-orange-300' : 'text-orange-700'}`}>
                            {appt.display_number}
                          </span>
                        )}
                        {isCurrentItem && (
                          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${isDark ? 'bg-orange-900/40 text-orange-300' : 'bg-orange-100 text-orange-700'}`}>
                            <Activity className="h-2.5 w-2.5" /> Active
                          </span>
                        )}
                        <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${colors.badge}`}>
                          {formatStatusLabel(appt.status)}
                        </span>
                      </div>
                      <p className={`mt-1 text-sm font-medium ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                        {appt.title}
                      </p>
                      <div className={`mt-1 flex flex-wrap items-center gap-3 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {(startTime || endTime) && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {startTime}{endTime ? ` – ${endTime}` : ''}
                          </span>
                        )}
                        {appt.contact_name && (
                          <span>{appt.contact_name}</span>
                        )}
                        {(appt.location_name || appt.location_address) && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {appt.location_name || appt.location_address}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

    </div>
  );
}
